import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getCurrencyRates, convertCurrency, getSupportedCurrencies } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowsLeftRight, Globe, TrendUp, TrendDown } from '@phosphor-icons/react';

export default function CurrencyPage() {
  const [rates, setRates] = useState({});
  const [currencies, setCurrencies] = useState([]);
  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [loading, setLoading] = useState(true);
  const [convertFrom, setConvertFrom] = useState('USD');
  const [convertTo, setConvertTo] = useState('EUR');
  const [convertAmount, setConvertAmount] = useState('1000');
  const [convertResult, setConvertResult] = useState(null);

  useEffect(() => { fetchData(); }, [baseCurrency]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ratesRes, currRes] = await Promise.all([getCurrencyRates(baseCurrency), getSupportedCurrencies()]);
      setRates(ratesRes.data.rates);
      setCurrencies(currRes.data.currencies);
    } catch (e) { toast.error('Failed to load rates'); }
    finally { setLoading(false); }
  };

  const handleConvert = async () => {
    try {
      const res = await convertCurrency({ amount: parseFloat(convertAmount), from: convertFrom, to: convertTo });
      setConvertResult(res.data);
    } catch (e) { toast.error('Conversion failed'); }
  };

  const currencySymbol = (code) => currencies.find(c => c.code === code)?.symbol || code;

  if (loading) {
    return (<Layout title="Multi-Currency"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin"></div></div></Layout>);
  }

  return (
    <Layout title="Multi-Currency">
      <div data-testid="currency-page" className="space-y-6">
        {/* Converter */}
        <div className="bg-white border border-zinc-200 rounded-sm p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2"><ArrowsLeftRight size={18} weight="duotone" /> Currency Converter</h3>
          <div className="flex items-end gap-4">
            <div className="flex-1 space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Amount</Label>
              <Input type="number" value={convertAmount} onChange={(e) => setConvertAmount(e.target.value)} className="rounded-sm font-mono" data-testid="convert-amount" />
            </div>
            <div className="w-32 space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">From</Label>
              <Select value={convertFrom} onValueChange={setConvertFrom}>
                <SelectTrigger className="rounded-sm" data-testid="convert-from"><SelectValue /></SelectTrigger>
                <SelectContent>{currencies.map(c => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <ArrowsLeftRight size={20} className="text-zinc-400 mb-2" />
            <div className="w-32 space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">To</Label>
              <Select value={convertTo} onValueChange={setConvertTo}>
                <SelectTrigger className="rounded-sm" data-testid="convert-to"><SelectValue /></SelectTrigger>
                <SelectContent>{currencies.map(c => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={handleConvert} className="bg-zinc-950 text-white rounded-sm" data-testid="convert-button">Convert</Button>
          </div>
          {convertResult && (
            <div className="mt-4 p-4 bg-zinc-50 border border-zinc-200 rounded-sm" data-testid="convert-result">
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-xs text-zinc-500">Original</p>
                  <p className="text-lg font-mono font-bold">{currencySymbol(convertResult.original.currency)}{convertResult.original.amount.toLocaleString()}</p>
                </div>
                <ArrowsLeftRight size={20} className="text-zinc-400" />
                <div>
                  <p className="text-xs text-zinc-500">Converted</p>
                  <p className="text-lg font-mono font-bold text-emerald-600">{currencySymbol(convertResult.converted.currency)}{convertResult.converted.amount.toLocaleString()}</p>
                </div>
                <div className="ml-auto text-right">
                  <p className="text-xs text-zinc-500">Rate</p>
                  <p className="font-mono text-sm">{convertResult.rate.toFixed(4)}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Exchange Rates Table */}
        <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Globe size={18} weight="duotone" className="text-zinc-600" />
              <h3 className="font-semibold">Exchange Rates</h3>
            </div>
            <Select value={baseCurrency} onValueChange={setBaseCurrency}>
              <SelectTrigger className="w-32 rounded-sm" data-testid="base-currency-select"><SelectValue /></SelectTrigger>
              <SelectContent>{currencies.map(c => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full table-dense">
              <thead>
                <tr className="text-left">
                  <th className="px-6 pt-4">Currency</th>
                  <th className="px-6 pt-4">Code</th>
                  <th className="px-6 pt-4 text-right">Rate (1 {baseCurrency})</th>
                  <th className="px-6 pt-4 text-right">Inverse</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(rates).map(([code, rate]) => {
                  const curr = currencies.find(c => c.code === code);
                  return (
                    <tr key={code} className="hover:bg-zinc-50">
                      <td className="px-6 text-sm">{curr?.symbol} {curr?.name || code}</td>
                      <td className="px-6 text-sm font-mono">{code}</td>
                      <td className="px-6 text-right font-mono text-sm font-medium">{rate.toFixed(4)}</td>
                      <td className="px-6 text-right font-mono text-sm text-zinc-500">{(1/rate).toFixed(4)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
